import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useState } from 'react'
import { Button } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { PageTitle } from '../components/page-title'
import { useAuth } from '../auth'
import { useTitle } from '../hooks/use-title'
import { CONNECT_SITE, INDEX_SITE } from '../user/permission'
import { SitesOverview } from '../site/components/sites-overview'
import { useSites } from '../site/hooks'
import { AddSiteModal } from '../site/components/add-site-modal'
import { z } from 'zod'

export const Route = createFileRoute('/_auth/_main/websites/')({
    // `add` opens the dialog to add a website, for customers who chose the website to start with.
    validateSearch: z.object({
        add: z.boolean().optional(),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_SITE)) {
            throw redirect({ to: '/dashboard' })
        }
    },
    component: Websites,
})

function Websites() {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data: sites } = useSites()
    const { add } = Route.useSearch()
    const navigate = useNavigate()
    const canAdd = can(CONNECT_SITE)
    const [adding, setAdding] = useState(!!add && canAdd)

    // The dialog is open now: take `add` out of the address, so a reload shows the page itself.
    useEffect(() => {
        if (add) {
            navigate({ to: '/websites', search: {}, replace: true })
        }
    }, [add, navigate])

    // Without websites, the overview itself shows the button to add the first one.
    const showsAddButton = canAdd && !!sites?.length

    useTitle(t('Websites'))

    return (
        <>
            <PageTitle
                title={t('Websites')}
                subtitle={t("Evaa answers your visitors' questions, based on what is on your website.")}
                extra={
                    showsAddButton && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setAdding(true)}
                        >
                            {t('Add website')}
                        </Button>
                    )
                }
            />
            <SitesOverview onAdd={canAdd ? () => setAdding(true) : undefined} />
            <AddSiteModal
                open={adding}
                onClose={() => setAdding(false)}
            />
        </>
    )
}
